import React, { useEffect, useState, useCallback } from 'react';
import { pagesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Edit, Trash2, Save, Plus, Settings } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';

export default function PagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPage, setEditPage] = useState(null);
  const [newPage, setNewPage] = useState(null);

  const loadPages = useCallback(async () => {
    try {
      const res = await pagesAPI.list();
      setPages(res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  const handleSavePage = async () => {
    if (!editPage) return;
    try {
      await pagesAPI.update(editPage.slug, {
        titulo: editPage.titulo,
        contenido: editPage.contenido,
        visible_en_menu: editPage.visible_en_menu,
        orden_menu: editPage.orden_menu
      });
      setEditPage(null);
      loadPages();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCreatePage = async () => {
    if (!newPage) return;
    try {
      await pagesAPI.create(newPage);
      setNewPage(null);
      loadPages();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm('Eliminar esta pagina?')) return;
    try { await pagesAPI.delete(slug); loadPages(); } catch {}
  };

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="pages-page">
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-dark">Paginas CMS</h1>
            <p className="text-sm font-sans text-gray-400 mt-1">Gestiona las paginas estaticas del sitio</p>
          </div>
          <Button onClick={() => setNewPage({ slug: '', titulo: '', contenido: '', visible_en_menu: false, orden_menu: 0 })} className="font-sans bg-slate-dark" data-testid="create-page-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva Pagina
          </Button>
        </div>

        <div className="space-y-3">
          {pages.map((p, i) => (
            <div key={p.slug} className="p-4 bg-white border border-gray-100 rounded-sm flex items-center justify-between" data-testid={`page-row-${i}`}>
              <div>
                <h3 className="font-sans font-medium text-slate-dark text-sm">{p.titulo}</h3>
                <p className="text-xs text-gray-400 mt-0.5">/{p.slug} {p.visible_en_menu ? '(En menu)' : ''}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditPage({ ...p })} data-testid={`edit-page-${i}`}><Edit className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDeletePage(p.slug)} data-testid={`delete-page-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
          {pages.length === 0 && (
            <div className="text-center py-12 bg-white rounded-sm border border-gray-100">
              <Settings className="w-10 h-10 text-gray-200 mx-auto mb-3" strokeWidth={1} />
              <p className="font-sans text-gray-400">No hay paginas creadas aun.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!editPage || !!newPage} onOpenChange={() => { setEditPage(null); setNewPage(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif">{editPage ? 'Editar Pagina' : 'Nueva Pagina'}</DialogTitle></DialogHeader>
          {(editPage || newPage) && (
            <div className="space-y-4">
              {newPage && (
                <div>
                  <label className="text-sm font-sans font-medium">Slug (URL)</label>
                  <Input value={newPage.slug} onChange={e => setNewPage(p => ({ ...p, slug: e.target.value }))} className="mt-1 font-sans" placeholder="mi-pagina" data-testid="page-slug-input" />
                </div>
              )}
              <div>
                <label className="text-sm font-sans font-medium">Titulo</label>
                <Input
                  value={(editPage || newPage).titulo}
                  onChange={e => (editPage ? setEditPage : setNewPage)(p => ({ ...p, titulo: e.target.value }))}
                  className="mt-1 font-sans"
                  data-testid="page-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-sans font-medium mb-2 block">Contenido</label>
                <RichTextEditor
                  value={(editPage || newPage).contenido}
                  onChange={val => (editPage ? setEditPage : setNewPage)(p => ({ ...p, contenido: val }))}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm font-sans">
                  <input
                    type="checkbox"
                    checked={(editPage || newPage).visible_en_menu}
                    onChange={e => (editPage ? setEditPage : setNewPage)(p => ({ ...p, visible_en_menu: e.target.checked }))}
                    className="rounded"
                    data-testid="page-visible-toggle"
                  />
                  Visible en menu
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-sans">Orden:</label>
                  <Input
                    type="number"
                    value={(editPage || newPage).orden_menu}
                    onChange={e => (editPage ? setEditPage : setNewPage)(p => ({ ...p, orden_menu: parseInt(e.target.value) || 0 }))}
                    className="w-20 font-sans"
                  />
                </div>
              </div>
              <Button
                onClick={editPage ? handleSavePage : handleCreatePage}
                className="w-full bg-slate-dark font-sans"
                data-testid="save-page-btn"
              >
                <Save className="w-4 h-4 mr-1" /> {editPage ? 'Guardar Cambios' : 'Crear Pagina'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
